<template>
  <div
    class="element-editor image-toolbar-wrapper"
    :class="{'default-image': isPlaceholderImage}"
    :id="blockId"
    :style="blockStyle"
    v-lazy-load
  >
    <div
      class="image-placeholder w-full h-full absolute top-0 left-0"
      v-if="imagePlaceholderVisible"
      @click="openUploadImageModal"
    >
      <aha-icon
        name="image"
        viewBox="0 0 24 24"
        width="80"
        height="80"
        class="image-placeholder-icon"
      />
      <p class="image-placeholder-text">
        Click to add image
      </p>
    </div>

    <div
      v-if="!isPlaceholderImage"
      class="spinner-wrapper"
      :class="`spinner-wrapper--${theme}`"
    >
      <aha-spinner :variant="theme" size="xl"></aha-spinner>
    </div>

    <img
      v-show="!isPlaceholderImage"
      class="image-editor w-full h-full absolute top-0 left-0"
      :class="animateClass"
      :data-url="srcCache || src"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import EventBus from '@/eventBus'
import { isBlockExist } from '@/utils/canvas-block-layouts/common'

export default {
  name: 'ImageEditor',
  props: {
    src: {
      type: [String, Object],
      default: null,
    },
    srcCache: {
      type: String,
      default: null,
    },
    isPlaceholderImage: {
      type: Boolean,
      default: false,
    },
    blockId: {
      type: String,
      default: null,
    },
    blockStyle: {
      type: Object,
      default: null,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    animation: {
      type: String,
      default: '',
    },
    isInfinity: {
      type: Boolean,
      default: false,
    },
    duration: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      imageLoaded: false,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage', 'isTextColourLight']),
    ...mapState({
      slideActive: (state) => state.slideActive,
    }),
    animateClass() {
      if (!this.animation) return null
      return `animate__animated animate__${this.animation} ${this.isInfinity ? 'animate__infinite' : ''} ${this.duration ? `animate__delay-${this.duration}s` : ''} `
    },
    theme() {
      return this.isTextColourLight ? 'white' : 'dark'
    },
    customImageExist() {
      return isBlockExist(this.slideActive.canvasBlocks, (block) => {
        return block.content && block.content.src !== null && block.type === 'Image' && !block.content.isPlaceholderImage
      })
    },
    imagePlaceholderVisible() {
      return !this.customImageExist && this.isPlaceholderImage &&
        !this.preview && !this.isPresenting && !this.isBackstage
    },
  },
  watch: {
    src: function watchSrc(oldSrc, newSrc) {
      if (oldSrc !== newSrc) {
        this.imageLoaded = false
      }
    },
  },

  mounted() {
    this.$nextTick(() => { this.$emit('ready') })
  },

  destroyed() {
    this.imageLoaded = false
  },

  methods: {
    onImageLoad() {
      this.imageLoaded = true
    },
    openUploadImageModal() {
      EventBus.$emit('open-image-upload-modal', this.blockId)
    },
    onClickOutside() {
      this.$emit('onBlur')
    },
  },
}
</script>

<style lang="scss" scoped>
.element-editor.loaded {
  .spinner-wrapper {
    display: none;
  }
}
.spinner-wrapper {
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  justify-content: center;
  align-items: center;
}

.spinner-wrapper--dark {
  background: rgba(0, 0, 0, 0.05);

}
.spinner-wrapper--white {
  background: rgba(255, 255, 255, 0.1);

}

.image-placeholder {
  cursor: pointer;
  background: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23ccc' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  transition: all 0.2s ease;
  border-radius: 8px;
   .image-placeholder-text {
    @apply text-2xl;
  }
  .image-placeholder-icon {
    opacity: 0.5;
    margin-bottom: 12px;
    @apply text-secondary-40;
  }
  &:hover {
    background: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%234af' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");

  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

}
</style>
